import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import Lf23Loader from "../../components/Lf23Loader";
import MappaTratte, { COLORE_STATO, formattaDataOra } from "../ReportPubblico/MappaTratte";
import { API_URL, authHeaders, nf, formatData, AvvisoEsecuzioni, fotoImgUrl, Miniatura, AnteprimaFoto } from "./comuni";

/**
 * Vista "Mappa" del dettaglio percorso (task 547, sotto-tab Oggi e Storico): il giro su
 * mappa, tratte fatte in verde e non fatte in rosso; su un giro ancora aperto le non fatte
 * sono grigie "in attesa" (la regola sta nel backend, percorsoMappaService).
 *
 * La mappa e' quella del report di quartiere (MappaTratte: linee per stato, alone sulla
 * tratta scelta, foto nel punto di scatto). Accanto, l'elenco delle vie nell'ordine del
 * giro; scegliendo una via (dall'elenco o sulla mappa) l'elenco lascia il posto al suo
 * dettaglio con le miniature delle foto, che si ingrandiscono nel lightbox con le frecce.
 *
 * Non e' un modale: e' il contenuto del modale di dettaglio quando si sceglie "Mappa"
 * nell'intestazione (TabPercorsi). Chiuderlo, Esc compreso, e' compito del modale. Il
 * lightbox sta fuori dal modale (position fixed) e sopra di lui.
 */

const STATO = {
    effettuata: { label: "Fatta", plurale: "Fatte", cls: "badge-stato-completate" },
    non_effettuata: { label: "Non fatta", plurale: "Non fatte", cls: "badge-stato-noneseguite" },
    in_attesa: { label: "In attesa", plurale: "In attesa", cls: "badge-stato-lavorazione" },
    // Non fatta nel giro, fatta dopo da un giro di recupero: badge rosso tratteggiato dello Storico.
    recuperata: { label: "Recuperata dopo", plurale: "Recuperate dopo", cls: "badge-recupero" },
};

/** Pallino di stato (legenda ed elenco): pieno, oppure ad anello tratteggiato per "recuperata",
 *  come la sua linea sulla mappa. */
const PallinoStato = ({ stato }) => (stato === "recuperata"
    ? <i className="gp-mappa-dot tratteggiato" style={{ borderColor: COLORE_STATO.recuperata }} title={STATO.recuperata.label} />
    : <i className="gp-mappa-dot" style={{ background: COLORE_STATO[stato] }} title={STATO[stato]?.label} />);

const IconaFoto = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
);

const MappaPercorso = ({ percorso }) => {
    const [dati, setDati] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errore, setErrore] = useState(null);
    const [selezionata, setSelezionata] = useState(null);           // codice della tratta scelta
    const [selezioneDaMappa, setSelezioneDaMappa] = useState(false); // scelta col clic sulla mappa: niente zoom
    const [mostraFoto, setMostraFoto] = useState(true);
    const [zoom, setZoom] = useState(null);                          // lightbox: { foto: [...], indice }
    const latoRef = useRef(null);

    const carica = useCallback(async () => {
        setLoading(true);
        setErrore(null);
        try {
            const res = await axios.get(`${API_URL}/gestione-percorsi/percorsi/${percorso.id}/mappa`, { headers: authHeaders() });
            setDati(res.data);
        } catch (err) {
            console.error("Errore mappa percorso:", err);
            setErrore(err.response?.status === 404 ? "Percorso non trovato." : "Impossibile caricare la mappa del percorso.");
        } finally {
            setLoading(false);
        }
    }, [percorso.id]);

    useEffect(() => { carica(); }, [carica]);

    // Memo: MappaTratte ridisegna le linee quando cambia l'array, non a ogni render.
    const tratte = useMemo(() => dati?.tratte || [], [dati]);
    const meta = dati?.meta || {};
    const trattaSel = useMemo(() => tratte.find((t) => t.codice === selezionata) || null, [tratte, selezionata]);

    /* Le foto con una posizione vanno sulla mappa nel punto dello scatto, col colore dello
       stato della loro tratta. Quelle senza coordinate restano solo fra le miniature. */
    const fotoGeo = useMemo(() => tratte.flatMap((t) => t.foto
        .filter((f) => f.lat != null && f.lng != null)
        .map((f) => ({ ...f, stato: t.stato, tratta: t.strada, thumb: fotoImgUrl(f.key, 160) }))), [tratte]);

    // Stessa logica di ReportView: ritoccando la tratta scelta (toggle) la si lascia.
    const seleziona = useCallback((codice, { daMappa = false, toggle = false } = {}) => {
        setSelezioneDaMappa(daMappa);
        setSelezionata((prev) => (codice && prev === codice ? (toggle ? null : prev) : codice || null));
    }, []);

    // Sotto i 900px il pannello sta sotto la mappa: scegliendo una tratta lo si porta in vista.
    useEffect(() => {
        if (trattaSel && latoRef.current && window.innerWidth <= 900) {
            latoRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [trattaSel]);

    const apriFoto = useCallback((foto, indice = 0) => setZoom({ foto, indice }), []);
    const chiudiFoto = useCallback(() => setZoom(null), []);
    const cambiaFoto = useCallback((i) => setZoom((z) => (z ? { ...z, indice: i } : z)), []);
    const didascalia = (f) => [f.tratta, f.indirizzo, f.scattataIl && formattaDataOra(f.scattataIl)].filter(Boolean).join(" · ");

    const conteggi = [
        ["effettuata", meta.effettuate],
        ["non_effettuata", meta.nonEffettuate],
        ["recuperata", meta.recuperate],
        ["in_attesa", meta.inAttesa],
    ].filter(([s, n]) => !["in_attesa", "recuperata"].includes(s) || n > 0);   // compaiono solo se ci sono

    return (
        <div className="gp-mappa-percorso">
            {loading ? (
                <div className="gp-gallery-loading"><Lf23Loader testo="Caricamento mappa..." /></div>
            ) : errore ? (
                <div className="gp-box-empty">
                    {errore}
                    <div style={{ marginTop: 10 }}><button className="btn btn-ghost" onClick={carica}>Riprova</button></div>
                </div>
            ) : (
                <>
                    <AvvisoEsecuzioni meta={meta}
                        testoDanno="Sulla mappa ogni tratta risulterebbe non fatta." />

                    <div className="gp-mappa-legenda">
                        {conteggi.map(([s, n]) => (
                            <span key={s} className="gp-mappa-voce">
                                <PallinoStato stato={s} />
                                {STATO[s].plurale} <strong>{nf(n || 0)}</strong>
                            </span>
                        ))}
                        {fotoGeo.length > 0 && (
                            <button type="button" className={`gp-mappa-voce gp-mappa-toggle ${mostraFoto ? "attiva" : ""}`}
                                onClick={() => setMostraFoto((v) => !v)} aria-pressed={mostraFoto}
                                title={mostraFoto ? "Nascondi le foto dalla mappa" : "Mostra sulla mappa il punto in cui è stata scattata ogni foto"}>
                                <IconaFoto /> Foto sulla mappa <strong>{nf(fotoGeo.length)}</strong>
                            </button>
                        )}
                        {meta.senzaGeometria > 0 && (
                            <span className="gp-mappa-nota">
                                {nf(meta.senzaGeometria)} tratt{meta.senzaGeometria === 1 ? "a" : "e"} senza geometria: in elenco ma non sulla mappa
                            </span>
                        )}
                        {meta.nonInAnagrafica > 0 && (
                            <span className="gp-mappa-nota">
                                {nf(meta.nonInAnagrafica)} tratt{meta.nonInAnagrafica === 1 ? "a" : "e"} del giro non più in anagrafica
                            </span>
                        )}
                    </div>

                    <div className="gp-mappa-corpo">
                        <MappaTratte tratte={tratte} selezionata={selezionata} selezioneDaMappa={selezioneDaMappa}
                            onSeleziona={seleziona} foto={fotoGeo} fotoVisibili={mostraFoto} onApriFoto={apriFoto}
                            popup={false} className="gp-mappa-mappa" />

                        <div className="gp-mappa-lato" ref={latoRef}>
                            {trattaSel ? (
                                <DettaglioTratta t={trattaSel} onIndietro={() => seleziona(null)}
                                    onApriFoto={(i) => apriFoto(trattaSel.foto.map((f) => ({ ...f, tratta: trattaSel.strada })), i)} />
                            ) : tratte.length === 0 ? (
                                <div className="gp-box-empty">Nessuna via associata a questo percorso.</div>
                            ) : (
                                tratte.map((t, i) => (
                                    <button key={t.codice} type="button" className="gp-geo-riga gp-mappa-riga" onClick={() => seleziona(t.codice)}>
                                        <div className="gp-geo-riga-testa">
                                            <span className="gp-mappa-riga-via">
                                                <PallinoStato stato={t.stato} />
                                                <span className="gp-mappa-idx">{i + 1}</span>
                                                <span className="gp-geo-via">{t.strada || "(senza strada)"}</span>
                                            </span>
                                            <span className="gp-mappa-riga-meta">
                                                {t.foto.length > 0 && <span className="gp-mappa-nfoto" title={`${t.foto.length} foto`}><IconaFoto />{t.foto.length}</span>}
                                                {!t.geometria && <span className="gp-mappa-nogeo" title="Tratta senza geometria: non compare sulla mappa">no mappa</span>}
                                            </span>
                                        </div>
                                        <div className="gp-geo-riga-sub">{[t.tratto, t.codice].filter(Boolean).join(" · ")}</div>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </>
            )}

            {zoom && (
                <AnteprimaFoto foto={zoom.foto} indice={zoom.indice} onIndice={cambiaFoto} onChiudi={chiudiFoto}
                    src={(f) => fotoImgUrl(f.key, 1600)} didascalia={didascalia} />
            )}
        </div>
    );
};

/** Pannello della tratta scelta: stato, passaggio, lunghezza e miniature delle foto. */
const DettaglioTratta = ({ t, onIndietro, onApriFoto }) => {
    const stato = STATO[t.stato] || { label: t.stato, cls: "badge-neutral" };
    return (
        <div className="gp-mappa-dettaglio">
            <button type="button" className="btn btn-ghost gp-new-btn gp-mappa-indietro" onClick={onIndietro}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                Tutte le vie
            </button>
            <div className="gp-mappa-dettaglio-testa">
                <div style={{ minWidth: 0 }}>
                    <div className="gp-geo-via">{t.strada || "(senza strada)"}</div>
                    <div className="gp-geo-riga-sub">{[t.tratto, t.codice].filter(Boolean).join(" · ")}</div>
                </div>
                <span className={`badge ${stato.cls}`}>{stato.label}</span>
            </div>
            <div className="gp-detail-grid gp-mappa-info">
                <div>
                    <span className="gp-detail-label">Passaggio</span>
                    <span className="gp-detail-val">{t.fattoIl ? formattaDataOra(t.fattoIl) : "—"}</span>
                </div>
                <div>
                    <span className="gp-detail-label">Lunghezza</span>
                    <span className="gp-detail-val">{t.lunghezzaMetri != null ? `${nf(t.lunghezzaMetri)} m` : "—"}</span>
                </div>
            </div>
            {t.recuperataDa && (
                <div className="gp-mappa-recupero">
                    Non fatta in questo giro · recuperata il <strong>{formatData(t.recuperataDa.data)}</strong>
                    {t.recuperataDa.nome && <> con {t.recuperataDa.nome}</>}
                    {t.recuperataDa.customId && <span className="gp-mappa-recupero-id"> ({t.recuperataDa.customId})</span>}
                </div>
            )}
            {!t.geometria && <div className="gp-mappa-nota">Tratta non georeferenziata: non compare sulla mappa.</div>}

            <div className="section-label">Foto ({t.foto.length})</div>
            {t.foto.length === 0 ? (
                <div className="gp-box-empty">Nessuna foto per questa tratta.</div>
            ) : (
                <div className="gp-mappa-foto-griglia">
                    {t.foto.map((f, i) => (
                        <button key={f.key} type="button" className="gp-mappa-foto" onClick={() => onApriFoto(i)}
                            title={f.scattataIl ? `Scattata il ${formattaDataOra(f.scattataIl)}` : "Apri foto"}>
                            <Miniatura src={fotoImgUrl(f.key, 300)} alt={`${t.strada} – foto ${i + 1}`} />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MappaPercorso;
